import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-primary-900 text-white hover:bg-primary-950 disabled:bg-zinc-100 disabled:text-zinc-400',
        destructive:
          'bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-error-600 disabled:bg-zinc-100 disabled:text-zinc-400',
        outline:
          'flex gap-2 items-center border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground disabled:bg-zinc-100 disabled:text-zinc-400',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        fullWidth: 'h-11 px-4 w-full py-2 rounded-full',
        sm: 'h-8 rounded-full px-8 text-xs',
        lg: 'h-10 rounded-full px-14',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
